import React, { useMemo, useState } from 'react';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import Papa from 'papaparse';
import { Radio, RadioGroup, FormControlLabel } from '@mui/material';
import { ApiService } from '../../services/ApiService';
import type {
    FeedbackPayload,
    PositiveCategory,
    NegativeCategory,
    FeedbackType,
} from '../../services/dto/feedback';
import { useChatMCPAuth } from '../../contexts/ChatMCPAuthContext';
import './ChatFeedback.css';

type ChatFeedbackProps = {
    promptId?: string;
    semanticModel?: string;
    accountName?: string;
    className?: string;
    tableData?: {
        columns?: any[];
        data?: any[];
        columnMetadata?: any[];
    };
};

const positiveCategories: { value: PositiveCategory; label: string }[] = [
    { value: 'accurate_answer', label: 'Accurate answer' },
    { value: 'insightful', label: 'Insightful' },
    { value: 'saved_time', label: 'Saved time' },
];

const negativeCategories: { value: NegativeCategory; label: string }[] = [
    { value: 'inaccurate_answer', label: 'Inaccurate answer' },
    { value: 'unclear', label: 'Unclear' },
    { value: 'missing_context', label: 'Missing context' },
];

const ChatFeedback: React.FC<ChatFeedbackProps> = ({
    promptId,
    semanticModel,
    accountName,
    className,
    tableData,
}) => {
    // Get auth context using React Context - no prop drilling needed!
    const authContext = useChatMCPAuth();

    const [selectedType, setSelectedType] = useState<FeedbackType | null>(null);
    const [negCategory, setNegCategory] = useState<NegativeCategory>('unclear');
    const [posCategory, setPosCategory] =
        useState<PositiveCategory>('saved_time');
    const [text, setText] = useState('');
    const [toastOpen, setToastOpen] = useState(false);
    const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
    const [copied, setCopied] = useState(false);
    const [copying, setCopying] = useState(false);
    const isEnabled = useMemo(() => Boolean(promptId), [promptId]);
    const hasTableData = useMemo(() => Boolean(tableData?.data && tableData.data.length > 0), [tableData]);

    const handleSubmit = async (payload: FeedbackPayload) => {
        if (!isEnabled) return;
        try {
            // Use React Context to get auth - clean and simple!
            const token = authContext.accessToken || '';
            await ApiService.submitChatFeedback(token, payload);
            setToastOpen(true);
        } catch (e) {
            // underlying ApiService will toast errors; keep UI responsive
        }
    };

    const onThumbUp = () => {
        if (!promptId) return;
        setSelectedType('positive');
        setFeedbackSubmitted(false);
    };

    const onThumbDown = () => {
        if (!promptId) return;
        setSelectedType('negative');
        setFeedbackSubmitted(false);
    };

    const canSend = useMemo(() => {
        if (!selectedType) return false;
        if (selectedType === 'negative') return text.trim().length > 0; // required
        return true; // positive optional
    }, [selectedType, text]);

    const onSend = () => {
        if (!promptId || !selectedType) return;
        if (selectedType === 'positive') {
            handleSubmit({
                prompt_id: promptId,
                feedback_type: 'positive',
                feedback_category: posCategory,
                feedback_text: text || undefined,
                semantic_model: semanticModel,
                account_name: accountName,
            });
        } else {
            handleSubmit({
                prompt_id: promptId,
                feedback_type: 'negative',
                feedback_category: negCategory,
                feedback_text: text,
                semantic_model: semanticModel,
                account_name: accountName,
            });
        }
        // Reset form after sending feedback but keep the selected action icon
        setText('');
        setFeedbackSubmitted(true);
        // Don't reset selectedType - keep the thumbs up/down icon selected
    };

    // Helper function to flatten complex values for CSV
    const flattenComplexValue = (value: any): string => {
        if (value === null || value === undefined) return '';
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
            return String(value);
        }
        if (Array.isArray(value)) {
            // For arrays, join primitive values or show summary for complex arrays
            if (value.every(item => typeof item !== 'object' || item === null)) {
                return `[${value.join(', ')}]`;
            }
            return `[Array with ${value.length} items]`;
        }
        if (typeof value === 'object') {
            // For objects, check for common patterns
            if (value.coordinates && value.type) {
                return `${value.type} geometry`;
            }
            if (Object.hasOwn(value, 'a') || Object.hasOwn(value, 'x')) {
                return `Coordinate object`;
            }
            return `Object with ${Object.keys(value).length} properties`;
        }
        return String(value);
    };

    const copyToCSV = async () => {
        try {
            if (copying || copied || !hasTableData) return;

            setCopying(true);

            if (!tableData?.data || tableData.data.length === 0) {
                alert('No data to copy');
                setCopying(false);
                return;
            }

            // Prepare data for PapaParse - flatten complex objects
            const processedData = tableData.data.map((row: any) => {
                const processedRow: any = {};
                Object.keys(row).forEach(key => {
                    processedRow[key] = flattenComplexValue(row[key]);
                });
                return processedRow;
            });

            // Use PapaParse to convert to CSV with tab delimiter
            const csvContent = Papa.unparse(processedData, {
                delimiter: '\t',
                header: true,
                skipEmptyLines: 'greedy',
                quotes: false, // Don't quote values unless necessary
                quoteChar: '"',
                escapeChar: '"'
            });

            // Small delay for better UX
            await new Promise(resolve => setTimeout(resolve, 300));

            // Copy to clipboard
            await navigator.clipboard.writeText(csvContent);

            setCopying(false);
            setCopied(true);

            // Reset after 2 seconds
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            console.error('Failed to copy:', error);
            setCopying(false);
            alert('Copy failed. Please try again.');
        }
    };

    return (
        <div className={`feedback-container ${className ?? ''}`}>
            <div className="feedback-inner">
                <div className="feedback-actions">
                    <button
                        className={`feedback-btn ${selectedType === 'positive' ? 'active' : ''}`}
                        onClick={onThumbUp}
                        disabled={!isEnabled}
                        title="Thumbs up"
                    >
                        <ThumbUpAltOutlinedIcon />
                    </button>
                    <button
                        className={`feedback-btn ${selectedType === 'negative' ? 'active' : ''}`}
                        onClick={onThumbDown}
                        disabled={!isEnabled}
                        title="Thumbs down"
                    >
                        <ThumbDownAltOutlinedIcon />
                    </button>
                    {hasTableData && (
                        <button
                            className={`feedback-btn copy-csv-btn ${copied ? 'copied' : ''} ${copying ? 'copying' : ''}`}
                            onClick={copyToCSV}
                            disabled={copying || copied || !hasTableData}
                            title="Copy table data as CSV"
                        >
                            {copied ? (
                                <CheckCircleOutlinedIcon />
                            ) : copying ? (
                                <ContentCopyOutlinedIcon className="copying-spin" />
                            ) : (
                                <ContentCopyOutlinedIcon />
                            )}
                        </button>
                    )}
                    {toastOpen && (
                        <span className="feedback-thanks-message">
                            Thanks for the feedback!
                        </span>
                    )}
                </div>
            </div>

            {selectedType && !feedbackSubmitted && (
                <div className="feedback-editor">
                    <div className="feedback-category-selection">


                        {/* Radio Buttons */}
                        <div className="feedback-radio">
                            <RadioGroup
                                value={selectedType === 'positive' ? posCategory : negCategory}
                                onChange={(e) =>
                                    selectedType === 'positive'
                                        ? setPosCategory(e.target.value as PositiveCategory)
                                        : setNegCategory(e.target.value as NegativeCategory)
                                }
                                row
                            >
                                {(selectedType === 'positive'
                                    ? positiveCategories
                                    : negativeCategories
                                ).map((c) => (
                                    <FormControlLabel
                                        key={c.value}
                                        value={c.value}
                                        control={<Radio />}
                                        label={c.label}
                                        className="feedback-radio-option"
                                        sx={{
                                            '& .MuiFormControlLabel-label': {
                                                fontSize: '1.25rem', // 20px
                                                fontWeight: 'bold' // You can add other styles too
                                            },
                                        }}
                                    />
                                ))}
                            </RadioGroup>
                        </div>
                    </div>

                    <textarea
                        className="feedback-textarea"
                        placeholder={
                            selectedType === 'negative'
                                ? 'Leave feedback (required)'
                                : 'Leave feedback (optional)'
                        }
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        rows={3}
                    />





                    <button
                        className="feedback-send"
                        onClick={onSend}
                        disabled={!canSend}
                    >
                        Send
                    </button>
                </div>
            )}

            {/* <Snackbar
                status="success"
                show={toastOpen}
                delay={2500}
                onHideSnackbar={() => setToastOpen(false)}
            >
                Thanks for the feedback! This feedback will be shared with the owner of the model.
            </Snackbar> */}
        </div>
    );
};

export default ChatFeedback;
